import { FC } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import { InputField } from 'src/components/input-field';
import { ConfirmationDialog } from 'src/components/confirmation-dialog';
import { SuccessDialog } from 'src/components/success-dialog';
import { useFormik } from 'formik';
import { useDialog } from 'src/hooks/use-dialog';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Technician } from 'src/interfaces/new-technician/technician';
import { technicianApi } from 'src/api/newTechnician/technician';
import { ProfileStatus } from 'src/enums/common';
import { REPAIR_SERVICE_ID } from 'src/constants/repairRequest-constants';

interface TechnicianApprovalCardProps {
  technician: Technician;
  setLoading: (flag: boolean) => void;
}

export const TechnicianApprovalCard: FC<TechnicianApprovalCardProps> = (props) => {
  const { technician, setLoading } = props;
  const navigate = useNavigate();
  const [resubmitDialogOpen, handleOpenResubmitDialog, handleCloseResubmitDialog] = useDialog();
  const [rejectDialogOpen, handleOpenRejectDialog, handleCloseRejectDialog] = useDialog();
  const [approveDialogOpen, handleOpenApproveDialog, handleCloseApproveDialog] = useDialog();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      remark: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      remark: Yup.string().required('Remark is required'),
    }),
    onSubmit: async () => {

    }
  });

  const handleApprovalSubmit = async (status) => {
    // update the status for technician
    setLoading(true);
    const attributes = {
      remark: formik.values.remark,
      status,
      id: technician.id
    };
    await technicianApi.updateTechnicianStatus(attributes);
    if (status === 'Active') {
      handleOpenApproveDialog();
      setLoading(false);
    } else {
      handleCloseResubmitDialog();
      handleCloseRejectDialog();
      navigate('/dashboard/technicians/main/pending');
    }
  };

  const handleSuccessDialogClose = () => {
    handleCloseApproveDialog();
    navigate('/dashboard/technicians/main/verified');
  };

  const isApproveDisabled = ():boolean => {
    if ((technician.brandsProducts.length < 1
      && technician.services.filter((item) => item.id === REPAIR_SERVICE_ID).length > 0)
      || technician.serviceAreaIds.length < 1) {
      return true;
    }
    return !(formik.isValid && formik.dirty) || formik.isSubmitting;
  };

  return (
    technician
      ? (
        <>
          {(technician.status === ProfileStatus.PENDING)
       && (
       <Card
         variant="outlined"
         sx={{ mt: 1 }}
       >
         <CardContent
           sx={{ mt: -3 }}
         >
           <Grid
             container
             spacing={1}
             sx={{ mt: 2, mb: 2 }}
           >
             <Grid
               item
               md={2}
               xs={12}
               sm={12}
             >
               <Box>
                 <Typography
                   color="textPrimary"
                   sx={{ ml: 2, mt: 2 }}
                   variant="h6"
                 >
                   Approval
                 </Typography>
               </Box>
             </Grid>
             <Grid
               item
               md={3}
               xs={10}
               sm={10}
             >
               <InputField
                 error={Boolean(formik.touched.remark && formik.errors.remark)}
                 fullWidth
                 helperText={formik.touched.remark && formik.errors.remark}
                 label="Remark"
                 multiline
                 required
                 name="remark"
                 onBlur={formik.handleBlur}
                 onChange={formik.handleChange}
                 placeholder="Add remark"
                 rows={3}
                 value={formik.values.remark}
               />
             </Grid>
             <Box
               sx={{
                 display: 'grid',
                 gap: 5,
                 gridAutoFlow: 'column',
                 mt: 8,
                 ml: 8
               }}
             >
               <Grid
                 item
               >
                 <ConfirmationDialog
                   message="Are you sure you want to resubmit this profile?"
                   onCancel={handleCloseResubmitDialog}
                   onConfirm={() => handleApprovalSubmit('Resubmission')}
                   open={resubmitDialogOpen}
                   title="Are you sure ?"
                   variant="warning"
                 />
                 <Button
                   color="error"
                   size="large"
                   variant="outlined"
                   onClick={() => handleOpenResubmitDialog()}
                   disabled={formik.values.remark === ''}
                 >
                   Resubmit
                 </Button>
               </Grid>
               <Grid
                 item
               >
                 <ConfirmationDialog
                   message="Are you sure you want to reject this profile?"
                   onCancel={handleCloseRejectDialog}
                   onConfirm={() => handleApprovalSubmit('Rejected')}
                   open={rejectDialogOpen}
                   title="Are you sure ?"
                   variant="warning"
                 />
                 <Button
                   color="error"
                   size="large"
                   variant="contained"
                   disabled={formik.values.remark === ''}
                   onClick={() => handleOpenRejectDialog()}
                 >
                   Reject
                 </Button>
               </Grid>
               <Grid
                 item
               >
                 <SuccessDialog
                   message="You have successfully onboarded Technician"
                   onCancel={() => handleSuccessDialogClose()}
                   open={approveDialogOpen}
                   title="Verification Successful"
                   addInfo={`${technician.id}: ${technician.firstName} ${technician.lastName}`}
                 />
                 <Button
                   color="success"
                   size="large"
                   variant="contained"
                   onClick={() => handleApprovalSubmit('Active')}
                   disabled={isApproveDisabled()}
                 >
                   Approve
                 </Button>
               </Grid>
             </Box>
           </Grid>
         </CardContent>
       </Card>
       )}
        </>
      )
      : <></>
  );
};

TechnicianApprovalCard.propTypes = {
  // @ts-ignore
  technician: PropTypes.object,
  setLoading: PropTypes.func,
};
